import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Network,
  Activity,
  BarChart3,
  Bell,
  Blocks,
  Code2,
  Bot,
  Building2,
  CalendarDays,
  Cable,
  Check,
  ChevronDown,
  ChevronRight,
  Command,
  Database,
  CreditCard,
  FileBarChart,
  FileText,
  Folder,
  FolderOpen,
  Gauge,
  Globe2,
  Home,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  MessagesSquare,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Search,
  Settings,
  Share2,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Sun,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/lib/auth-context';
import { useTheme } from '@/lib/theme-context';
import { CommandPalette, type CommandItem } from '@/components/ui/CommandPalette';
import { DropdownSelect } from '@/components/ui/DropdownSelect';
import { UserAnimalAvatar } from '@/components/ui/UserAnimalAvatar';
import { TenantQuotaBanner } from '@/components/billing/TenantQuotaBanner';
import { TenantExpirationBanner } from '@/components/billing/TenantExpirationBanner';
import { ATLAS_LANGUAGES, useLanguage, type AtlasLanguage } from '@/lib/language-context';
import { agentsModuleEnabled, canAccessAgents, canAccessAppBench, canAccessPhysicalAi, canAccessScheduling } from '@/lib/product-flags';
import { APPBENCH_TENANT_NAVIGATION } from '@/pages/admin/appbench/navigation';
import { appName, surfaceIncludes } from '@/lib/app-surface';

interface NavigationItem {
  label: string;
  to: string;
  icon: LucideIcon;
  match?: (pathname: string) => boolean;
  accent?: 'purple';
  /** Heading shown above this item to start a new section within its product. */
  section?: string;
}

/** A product or workspace shown as one icon in the rail. */
type NavigationAreaId =
  | 'workspace'
  | 'agents'
  | 'physical-ai'
  | 'red-team'
  | 'appbench'
  | 'promptbench'
  | 'scheduling'
  | 'platform'
  | 'quality';

interface NavigationGroup {
  area: NavigationAreaId;
  label?: string;
  items: NavigationItem[];
  nested?: boolean;
  root?: NavigationItem;
}

interface NavigationArea {
  id: NavigationAreaId;
  label: string;
  icon: LucideIcon;
  to: string;
  groups: NavigationGroup[];
}

const NAVIGATION_AREA_ICONS: Record<NavigationAreaId, LucideIcon> = {
  workspace: Home,
  agents: Network,
  'physical-ai': Bot,
  'red-team': ShieldAlert,
  appbench: Gauge,
  promptbench: MessagesSquare,
  scheduling: CalendarDays,
  platform: Building2,
  quality: ShieldCheck,
};

/** Where each AppBench section group starts, in the order people work. */
const APPBENCH_NAVIGATION_SECTIONS: Partial<
  Record<(typeof APPBENCH_TENANT_NAVIGATION)[number]['id'], string>
> = {
  dashboard: 'Overview',
  datasets: 'Set up',
  benchmarks: 'Run',
  reports: 'Review',
};

const navigationPath = (to: string) => to.split('?')[0]!;
const itemMatches = (item: NavigationItem, pathname: string) =>
  item.match
    ? item.match(pathname)
    : pathname === navigationPath(item.to) || pathname.startsWith(`${navigationPath(item.to)}/`);

interface ActiveProjectSummary {
  id: string;
  name: string;
  project_type?: string;
}

type ThemePreset = 'studio' | 'forest' | 'violet' | 'sunset' | 'graphite';
type AtlasFont = 'inter' | 'manrope' | 'ibm-plex' | 'source-sans' | 'space-grotesk';
type SidebarCollapseMode = 'off-canvas' | 'icons';

const APPBENCH_NAVIGATION_ICONS: Record<
  (typeof APPBENCH_TENANT_NAVIGATION)[number]['id'],
  LucideIcon
> = {
  dashboard: Gauge,
  datasets: Database,
  apps: Blocks,
  connections: Cable,
  benchmarks: SlidersHorizontal,
  runs: ListChecks,
  'live-monitor': Activity,
  reports: BarChart3,
  'usage-budgets': FileBarChart,
  'settings-audit': Settings,
};

const THEME_PRESETS: Array<{ id: ThemePreset; name: string; colors: [string, string, string] }> = [
  { id: 'studio', name: 'Studio Blue', colors: ['#3772ff', '#6d95ff', '#e3ebff'] },
  { id: 'forest', name: 'Forest Flow', colors: ['#059669', '#34d399', '#d1fae5'] },
  { id: 'violet', name: 'Violet Signal', colors: ['#7c3aed', '#a78bfa', '#ede9fe'] },
  { id: 'sunset', name: 'Sunset Coral', colors: ['#df2935', '#fb7185', '#ffe4e6'] },
  { id: 'graphite', name: 'Graphite', colors: ['#27272a', '#71717a', '#e4e4e7'] },
];

const FONT_OPTIONS: Array<{ id: AtlasFont; name: string }> = [
  { id: 'inter', name: 'Inter' },
  { id: 'manrope', name: 'Manrope' },
  { id: 'ibm-plex', name: 'IBM Plex Sans' },
  { id: 'source-sans', name: 'Source Sans 3' },
  { id: 'space-grotesk', name: 'Space Grotesk' },
];

const isProjectWorkspacePath = (pathname: string) => /^\/admin\/projects\/[^/]+/.test(pathname);

function pageLabel(pathname: string, t: (key: any) => string) {
  if (pathname === '/') return t('overview');
  const labels: Array<[RegExp, string]> = [
    [/^\/admin\/dashboard/, t('dashboard')],
    [/^\/admin\/account\/projects/, t('projects')],
    [/^\/admin\/projects\/new/, t('createProject')],
    [/^\/admin\/projects\/[^/]+\/analytics/, t('projectAnalytics')],
    [/^\/admin\/projects\/[^/]+/, t('projectWorkspace')],
    [/^\/admin\/organization/, t('myOrganization')],
    [/^\/admin\/red-team/, t('redTeam')],
    [/^\/admin\/appbench/, 'AppBench'],
    [/^\/promptbench/, 'PromptBench'],
    [/^\/scheduling/, t('scheduling')],
    [/^\/qa\/dashboard/, t('qualityDashboard')],
    [/^\/super-admin\/appbench-datasets/, 'AppBench datasets'],
    [/^\/super-admin\/tenants/, t('tenantManagement')],
    [/^\/projects\/[^/]+/, t('projectWorkspace')],
    [/^\/profile/, t('profile')],
  ];
  return labels.find(([pattern]) => pattern.test(pathname))?.[1] || appName;
}

function roleLabel(role: string | undefined, t: (key: any) => string) {
  return (
    (
      {
        admin: t('administrator'),
        project_manager: t('projectManager'),
        recruiter: t('recruiter'),
        scheduler: t('scheduler'),
        qa: t('qualityAnalyst'),
        participant: t('annotator'),
        super_admin: t('superAdministrator'),
      } as Record<string, string>
    )[role || ''] || t('member')
  );
}

function SidebarLink({
  item,
  nested = false,
  onSelect,
}: {
  item: NavigationItem;
  nested?: boolean;
  onSelect: () => void;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      onClick={onSelect}
      className={({ isActive }) => {
        const active = item.match ? item.match(window.location.pathname) : isActive;
        return `group relative flex items-center gap-2.5 rounded-lg text-xs transition duration-200 ease-atlas ${nested ? 'ml-3 h-8 px-3' : 'h-9 px-3'} ${
          active
            ? item.accent === 'purple'
              ? 'bg-purple-100 font-medium text-purple-800 dark:bg-purple-950/60 dark:text-purple-200'
              : 'border border-atlas-border bg-atlas-subtle font-medium text-atlas-ink shadow-sm'
            : 'font-medium text-atlas-muted hover:bg-atlas-subtle hover:text-atlas-ink'
        }`;
      }}
    >
      <Icon className="h-[17px] w-[17px] shrink-0 stroke-[1.8]" />
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      <ChevronRight className="h-3.5 w-3.5 opacity-0 transition group-hover:opacity-50" />
    </NavLink>
  );
}

export function AppShell() {
  const { user, isAuthenticated, logout } = useAuth();
  const { theme, resolvedTheme, setTheme, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const profileMenuRef = useRef<HTMLDivElement | null>(null);
  const settingsMenuRef = useRef<HTMLDivElement | null>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    () => window.localStorage.getItem('atlas-sidebar') === 'collapsed',
  );
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [lastAreaId, setLastAreaId] = useState<NavigationAreaId>();
  const [commandOpen, setCommandOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [themePreset, setThemePreset] = useState<ThemePreset>(
    () => (window.localStorage.getItem('atlas-theme-preset') as ThemePreset) || 'studio',
  );
  const [atlasFont, setAtlasFont] = useState<AtlasFont>(
    () => (window.localStorage.getItem('atlas-font') as AtlasFont) || 'inter',
  );
  const [sidebarCollapseMode, setSidebarCollapseMode] = useState<SidebarCollapseMode>(
    () =>
      (window.localStorage.getItem('atlas-sidebar-collapse-mode') as SidebarCollapseMode) ||
      'icons',
  );
  const [participantProjects, setParticipantProjects] = useState<any[]>([]);
  const [redTeamMemberships, setRedTeamMemberships] = useState<any[]>([]);
  const [activeProject, setActiveProject] = useState<ActiveProjectSummary | null>(null);
  const [availableProjects, setAvailableProjects] = useState<ActiveProjectSummary[]>([]);

  const isParticipant =
    isAuthenticated &&
    !['admin', 'project_manager', 'super_admin', 'qa', 'recruiter', 'scheduler'].includes(user?.role || '');
  // Recruiters and schedulers only use Study Scheduling, so account extras like billing stay hidden.
  const isSchedulingStaff = user?.role === 'recruiter' || user?.role === 'scheduler';
  const isTenantReadOnly = user?.tenantAccessMode === 'expired_read_only';
  const isPublicPage = ['/login', '/signup', '/auth/action', '/privacy', '/terms'].includes(
    location.pathname,
  );
  const activeProjectId = location.pathname.match(/^\/admin\/projects\/([^/]+)/)?.[1] || null;
  const dashboardProjectId = new URLSearchParams(location.search).get('project_id') || '';
  const selectedProjectId = activeProjectId || dashboardProjectId;
  const isOffCanvasCollapsed = isSidebarCollapsed && sidebarCollapseMode === 'off-canvas';

  useEffect(() => {
    document.documentElement.dataset.preset = themePreset;
    window.localStorage.setItem('atlas-theme-preset', themePreset);
  }, [themePreset]);

  useEffect(() => {
    document.documentElement.dataset.font = atlasFont;
    window.localStorage.setItem('atlas-font', atlasFont);
  }, [atlasFont]);

  useEffect(() => {
    window.localStorage.setItem('atlas-sidebar-collapse-mode', sidebarCollapseMode);
  }, [sidebarCollapseMode]);

  useEffect(() => {
    if (!isParticipant || user?.products?.data_collection === false) return;
    apiClient
      .get('/participants/me/projects')
      .then((response) => setParticipantProjects(response.data.projects || []))
      .catch(() => {});
  }, [isParticipant, user?.products?.data_collection]);

  useEffect(() => {
    if (!isAuthenticated || !user?.products?.red_team) return;
    apiClient
      .get('/red-team/me')
      .then((response) => setRedTeamMemberships(response.data.memberships || []))
      .catch(() => {});
  }, [isAuthenticated, user?.products?.red_team]);

  useEffect(() => {
    if (
      !selectedProjectId ||
      !['admin', 'project_manager', 'super_admin'].includes(user?.role || '')
    ) {
      setActiveProject(null);
      return;
    }
    let current = true;
    apiClient
      .get(`/admin/projects/${selectedProjectId}`)
      .then((response) => {
        if (!current) return;
        const project = response.data.project as ActiveProjectSummary | undefined;
        setActiveProject(project || { id: selectedProjectId, name: t('currentProject') });
      })
      .catch(() => {
        if (current) setActiveProject({ id: selectedProjectId, name: t('currentProject') });
      });
    return () => {
      current = false;
    };
  }, [selectedProjectId, t, user?.role]);

  useEffect(() => {
    if (
      !isAuthenticated ||
      user?.products?.data_collection === false ||
      !['admin', 'project_manager', 'super_admin'].includes(user?.role || '')
    ) {
      setAvailableProjects([]);
      return;
    }
    let current = true;
    apiClient
      .get('/admin/projects')
      .then((response) => {
        if (!current) return;
        setAvailableProjects(
          (response.data.projects || []).map((project: ActiveProjectSummary) => ({
            id: project.id,
            name: project.name,
            project_type: project.project_type,
          })),
        );
      })
      .catch(() => {
        if (current) setAvailableProjects([]);
      });
    return () => {
      current = false;
    };
  }, [isAuthenticated, user?.products?.data_collection, user?.role]);

  useEffect(() => {
    setMobileSidebarOpen(false);
    setProfileOpen(false);
    setSettingsOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommandOpen((open) => !open);
      }
    };
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isDropdownSelection = Boolean(target.closest('[data-atlas-dropdown-portal]'));
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(target) &&
        !isDropdownSelection
      )
        setProfileOpen(false);
      if (
        settingsMenuRef.current &&
        !settingsMenuRef.current.contains(target) &&
        !isDropdownSelection
      )
        setSettingsOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('mousedown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('mousedown', onPointerDown);
    };
  }, []);

  const navigationGroups = useMemo<NavigationGroup[]>(() => {
    const groups: NavigationGroup[] = [];
    // Collections live only in Dexter; Orca never lists them, whatever the tenant has.
    const dataCollectionEnabled = surfaceIncludes('data_collection') && user?.products?.data_collection !== false;
    if (['admin', 'project_manager', 'super_admin'].includes(user?.role || '')) {
      const workspace: NavigationItem[] = [];
      if (dataCollectionEnabled && !isTenantReadOnly)
        workspace.push({
          label: t('dashboard'),
          to: '/admin/dashboard',
          icon: LayoutDashboard,
          match: (path) => path === '/admin/dashboard' && !selectedProjectId,
        });
      if (dataCollectionEnabled)
        workspace.push({
          label: t('projects'),
          to: '/admin/account/projects',
          icon: Folder,
          match: (path) =>
            path.startsWith('/admin/account/projects') ||
            isProjectWorkspacePath(path) ||
            (!isTenantReadOnly &&
              (path === '/admin/projects/new' ||
                (path === '/admin/dashboard' && Boolean(selectedProjectId)))),
        });
      if (workspace.length) groups.push({ area: 'workspace', label: t('workspace'), items: workspace });
      if (selectedProjectId && dataCollectionEnabled) {
        const base = `/admin/projects/${selectedProjectId}`;
        const projectItems: NavigationItem[] = [
          { label: t('overview'), to: `${base}/overview`, icon: Activity },
          { label: t('files'), to: `${base}/files`, icon: FolderOpen },
        ];
        if (!isTenantReadOnly) {
          projectItems.splice(1, 0, { label: t('queue'), to: `${base}/queue`, icon: ListChecks });
          if (activeProject?.project_type === 'audio')
            projectItems.push({
              label: t('transcripts'),
              to: `${base}/transcripts`,
              icon: FileText,
            });
          projectItems.push(
            { label: t('workflow'), to: `${base}/workflow`, icon: Share2 },
            { label: t('analytics'), to: `${base}/analytics`, icon: BarChart3 },
            { label: t('users'), to: `${base}/users`, icon: Users },
            { label: t('agents'), to: `${base}/agents`, icon: Bot },
            { label: t('settings'), to: `${base}/settings`, icon: Settings },
          );
        }
        groups.push({
          area: 'workspace',
          label: activeProject?.name || t('project'),
          items: projectItems,
          nested: true,
        });
      }
      if (!isTenantReadOnly && user?.role !== 'project_manager')
        groups.push({
          area: 'workspace',
          items: [{ label: t('myOrganization'), to: '/admin/organization', icon: Building2 }],
        });
      if (canAccessAgents(user)) {
        groups.push({
          area: 'agents',
          items: [
            {
              label: 'Projects',
              to: '/agents/projects',
              icon: Activity,
              match: (path) => path === '/agents' || path.startsWith('/agents/projects'),
            },
            { label: 'Agents', to: '/agents/agents', icon: Bot, match: (path) => path.startsWith('/agents/agents') || path.startsWith('/agents/templates') },
            { label: 'Automations', to: '/agents/automations', icon: CalendarDays, match: (path) => path.startsWith('/agents/automations') },
            { label: 'Environments', to: '/agents/environments', icon: Database, match: (path) => path.startsWith('/agents/environments') },
            ...(user?.role === 'admin' || user?.role === 'project_manager' ? [{ label: 'Approvals', to: '/agents/approvals', icon: ShieldCheck, match: (path: string) => path.startsWith('/agents/approvals') }] : []),
            ...(user?.role === 'admin' || user?.role === 'project_manager' ? [{ label: 'Apps', to: '/agents/apps', icon: Blocks, match: (path: string) => path.startsWith('/agents/apps') }] : []),
            ...(user?.role === 'admin' ? [{ label: 'Keys', to: '/agents/keys', icon: Cable, match: (path: string) => path.startsWith('/agents/keys') }] : []),
            ...(user?.role === 'admin' ? [{ label: 'Developers', to: '/agents/developers', icon: Code2, match: (path: string) => path.startsWith('/agents/developers') }] : []),
            ...(user?.role === 'super_admin'
              ? []
              : [{ label: 'Billing', to: '/agents/billing', icon: CreditCard, match: (path: string) => path.startsWith('/agents/billing') }]),
          ],
        });
      }
      if (canAccessPhysicalAi(user)) {
        groups.push({
          area: 'physical-ai',
          items: [{ label: 'Physical AI', to: '/admin/physical-ai', icon: Database }],
        });
      }
      if (!isTenantReadOnly && user?.role === 'admin' && user.products?.red_team && surfaceIncludes('red_team')) {
        groups.push({
          area: 'red-team',
          items: [
            {
              label: t('redTeam'),
              to: '/admin/red-team/campaigns',
              icon: ShieldAlert,
              accent: 'purple',
              match: (path) => path.startsWith('/admin/red-team'),
            },
          ],
        });
      }
      if (!isTenantReadOnly && canAccessAppBench(user)) {
        groups.push({
          area: 'appbench',
          root: {
            label: 'AppBench',
            to: '/admin/appbench/dashboard',
            icon: Gauge,
            match: (path) => path.startsWith('/admin/appbench'),
          },
          items: APPBENCH_TENANT_NAVIGATION.map((item) => ({
            label: item.label,
            to: item.path,
            icon: APPBENCH_NAVIGATION_ICONS[item.id],
            section: APPBENCH_NAVIGATION_SECTIONS[item.id],
          })),
        });
      }
    }
    if (user?.products?.promptbench && surfaceIncludes('promptbench')) {
      const project = new URLSearchParams(location.search).get('project');
      const query = project ? `?project=${encodeURIComponent(project)}` : '';
      const tnsEnabled = import.meta.env.VITE_PROMPTBENCH_TNS_ENABLED === 'true';
      const isAdmin = user.role === 'admin';
      // Grouped in the order people work, matching AppBench: set up, run, review, then manage.
      const sections: Array<{
        id: string;
        label: string;
        icon: LucideIcon;
        section?: string;
        match?: (path: string) => boolean;
      }> = [
        {
          id: 'projects',
          label: 'Overview',
          icon: Folder,
          section: 'Overview',
          match: (path) => path === '/promptbench/projects' || path === '/promptbench',
        },
        ...(isAdmin
          ? [
              {
                id: 'database',
                label: 'Database',
                icon: Database,
                section: 'Set up',
                match: (path: string) =>
                  path === '/promptbench/database' || path === '/promptbench/collections',
              },
              { id: 'connections', label: 'Models', icon: Cable },
            ]
          : []),
        { id: 'sessions', label: 'Sessions', icon: ListChecks, section: 'Run' },
        ...(isAdmin && tnsEnabled ? [{ id: 'tns', label: 'TnS Bench', icon: ShieldCheck }] : []),
        {
          id: 'dashboard',
          label: 'Dashboard',
          icon: BarChart3,
          section: 'Review',
          match: (path) => path === '/promptbench/dashboard' || path === '/promptbench/reports',
        },
        ...(isAdmin
          ? [
              { id: 'members', label: 'Members', icon: Users, section: 'Manage' },
              { id: 'settings', label: 'Settings', icon: Settings },
            ]
          : []),
      ];
      groups.push({
        area: 'promptbench',
        root: {
          label: 'PromptBench',
          to: `/promptbench/projects${query}`,
          icon: Gauge,
          match: (path) => path.startsWith('/promptbench'),
        },
        items: sections.map((item) => ({
          label: item.label,
          to: `/promptbench/${item.id}${query}`,
          icon: item.icon,
          section: item.section,
          match: item.match ?? ((path: string) => path === `/promptbench/${item.id}`),
        })),
      });
    }
    if (!isTenantReadOnly && canAccessScheduling(user))
      groups.push({
        area: 'scheduling',
        items: [
          {
            label: t('studies'),
            to: '/scheduling',
            icon: CalendarDays,
            match: (path) => path.startsWith('/scheduling'),
          },
        ],
      });
    if (user?.role === 'super_admin')
      groups.push({
        area: 'platform',
        label: t('platform'),
        items: [
          { label: t('tenantManagement'), to: '/super-admin/tenants', icon: Settings },
          ...(surfaceIncludes('appbench')
            ? [{ label: 'AppBench datasets', to: '/super-admin/appbench-datasets', icon: Database }]
            : []),
          ...(agentsModuleEnabled ? [{ label: 'Orca credits', to: '/super-admin/orca-credits', icon: CreditCard }] : []),
        ],
      });
    if (user?.role === 'qa' && surfaceIncludes('data_collection'))
      groups.push({
        area: 'quality',
        label: t('quality'),
        items: [{ label: t('qualityDashboard'), to: '/qa/dashboard', icon: ShieldCheck }],
      });
    if (isParticipant) {
      groups.push({ area: 'workspace', items: [{ label: t('home'), to: '/', icon: Home }] });
      if (participantProjects.length)
        groups.push({
          area: 'workspace',
          label: t('projects'),
          items: participantProjects.map((project) => ({
            label: project.name,
            to: `/projects/${project.id}/overview`,
            icon: Folder,
            match: (path: string) => path.startsWith(`/projects/${project.id}`),
          })),
        });
      if (redTeamMemberships.length)
        groups.push({
          area: 'workspace',
          label: t('redTeamCampaigns'),
          items: redTeamMemberships.map((membership) => ({
            label: membership.campaign_name,
            to: `/red-team/campaigns/${membership.campaign_id}/workspace`,
            icon: ShieldAlert,
            accent: 'purple' as const,
          })),
        });
    }
    return groups.filter((group) => group.items.length);
  }, [
    location.search,
    activeProject,
    isParticipant,
    isTenantReadOnly,
    participantProjects,
    redTeamMemberships,
    selectedProjectId,
    t,
    user,
  ]);

  const navigationAreas = useMemo<NavigationArea[]>(() => {
    const labels: Record<NavigationAreaId, string> = {
      workspace: isParticipant ? t('home') : t('workspace'),
      agents: 'Agents',
      'physical-ai': 'Physical AI',
      'red-team': t('redTeam'),
      appbench: 'AppBench',
      promptbench: 'PromptBench',
      scheduling: t('scheduling'),
      platform: t('platform'),
      quality: t('quality'),
    };
    const areas: NavigationArea[] = [];
    for (const group of navigationGroups) {
      let area = areas.find((item) => item.id === group.area);
      if (!area) {
        area = {
          id: group.area,
          label: labels[group.area],
          icon: NAVIGATION_AREA_ICONS[group.area],
          to: group.root?.to || group.items[0]!.to,
          groups: [],
        };
        areas.push(area);
      }
      area.groups.push(group);
    }
    // The user's own home comes first, then products in a fixed order, then administration.
    const order: NavigationAreaId[] = [
      'workspace',
      'quality',
      'agents',
      'physical-ai',
      'red-team',
      'appbench',
      'promptbench',
      'scheduling',
      'platform',
    ];
    return areas.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
  }, [isParticipant, navigationGroups, t]);

  // The area that owns the current page; pages outside every area keep the last one open.
  const matchedAreaId = navigationAreas.find((area) =>
    area.groups.some(
      (group) =>
        group.root?.match?.(location.pathname) ||
        group.items.some((item) => itemMatches(item, location.pathname)),
    ),
  )?.id;
  useEffect(() => {
    if (matchedAreaId) setLastAreaId(matchedAreaId);
  }, [matchedAreaId]);
  const activeArea =
    navigationAreas.find((area) => area.id === (matchedAreaId || lastAreaId)) || navigationAreas[0];

  const commandItems = useMemo<CommandItem[]>(() => {
    const navigation = navigationGroups.flatMap((group) =>
      group.items.map((item) => ({
        label: item.label,
        description: `${t('open')} ${item.label}`,
        group: group.label || group.root?.label || t('navigation'),
        icon: item.icon,
        action: () => navigate(item.to),
      })),
    );
    if (
      !isTenantReadOnly &&
      ['admin', 'project_manager', 'super_admin'].includes(user?.role || '') &&
      user?.products?.data_collection !== false
    )
      navigation.unshift({
        label: t('createProject'),
        description: t('startNewProject'),
        group: t('actions'),
        icon: Plus,
        action: () => navigate('/admin/projects/new'),
      });
    return navigation;
  }, [isTenantReadOnly, navigate, navigationGroups, t, user]);

  const toggleSidebar = () => {
    const next = !isSidebarCollapsed;
    setIsSidebarCollapsed(next);
    window.localStorage.setItem('atlas-sidebar', next ? 'collapsed' : 'expanded');
  };
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const chooseProject = (projectId: string) => {
    setSettingsOpen(false);
    navigate(
      projectId
        ? isTenantReadOnly
          ? `/admin/projects/${projectId}/overview`
          : `/admin/dashboard?project_id=${projectId}`
        : isTenantReadOnly
          ? '/admin/account/projects'
          : '/admin/dashboard',
    );
  };

  const restoreAppearanceDefaults = () => {
    setThemePreset('studio');
    setAtlasFont('inter');
    setTheme('light');
    setSidebarCollapseMode('icons');
  };

  if (isPublicPage)
    return (
      <div className="min-h-screen bg-atlas-canvas font-sans text-atlas-ink">
        <Outlet />
      </div>
    );

  const renderSidebar = (forceExpanded = false) => {
    const collapsed = isSidebarCollapsed && !forceExpanded;
    const hidden = isOffCanvasCollapsed && !forceExpanded;
    const showWorkspaceTools = activeArea?.id === 'workspace';
    return (
      <div
        className={`flex h-full bg-[rgb(var(--atlas-sidebar))] transition-[width] duration-300 ease-atlas ${hidden ? 'w-0 overflow-hidden' : collapsed ? 'w-[56px]' : 'w-[272px]'}`}
      >
        <aside
          className="flex w-[56px] shrink-0 flex-col items-center border-r border-atlas-border"
          aria-label="Products"
        >
          <div className="flex h-[42px] w-full shrink-0 items-center justify-center border-b border-atlas-border">
            <Link
              to="/"
              className="grid h-7 w-7 place-items-center rounded-lg border border-atlas-border bg-atlas-surface text-atlas-ink"
              aria-label={`${appName} home`}
            >
              <Command className="h-4 w-4 stroke-[1.7]" />
            </Link>
          </div>
          <nav className="flex flex-1 flex-col items-center gap-1 overflow-y-auto py-3">
            {navigationAreas.map((area) => {
              const AreaIcon = area.icon;
              const active = area.id === activeArea?.id;
              return (
                <button
                  key={area.id}
                  type="button"
                  title={area.label}
                  aria-label={area.label}
                  aria-current={active ? 'true' : undefined}
                  onClick={() => {
                    setLastAreaId(area.id);
                    if (!active || !matchedAreaId) navigate(area.to);
                  }}
                  className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg transition duration-200 ease-atlas ${
                    active
                      ? 'border border-atlas-border bg-atlas-subtle text-atlas-ink shadow-sm'
                      : 'text-atlas-muted hover:bg-atlas-subtle hover:text-atlas-ink'
                  }`}
                >
                  <AreaIcon className="h-[17px] w-[17px] stroke-[1.8]" />
                </button>
              );
            })}
          </nav>
          <div className="flex h-14 shrink-0 items-center">
            <button
              onClick={toggleSidebar}
              className="atlas-icon-button hidden lg:inline-grid"
              aria-label={isSidebarCollapsed ? t('expandSidebar') : t('collapseSidebar')}
            >
              {isSidebarCollapsed ? (
                <PanelLeftOpen className="h-4 w-4" />
              ) : (
                <PanelLeftClose className="h-4 w-4" />
              )}
            </button>
          </div>
        </aside>

        {!collapsed && activeArea && (
          <aside
            className="flex w-[216px] shrink-0 flex-col border-r border-atlas-border"
            aria-label={`${activeArea.label} navigation`}
          >
            <div className="flex h-[42px] shrink-0 items-center border-b border-atlas-border px-4">
              <span className="truncate text-sm font-medium tracking-tight text-atlas-ink">
                {activeArea.label}
              </span>
            </div>
            <div className="space-y-2 px-3 pt-3">
              {showWorkspaceTools &&
                !isTenantReadOnly &&
                ['admin', 'project_manager', 'super_admin'].includes(user?.role || '') &&
                user?.products?.data_collection !== false && (
                  <button
                    onClick={() => navigate('/admin/projects/new')}
                    className="flex h-8 w-full items-center justify-center gap-1.5 rounded-lg bg-atlas-blue px-3 text-xs font-medium text-white shadow-sm transition hover:bg-atlas-lime-hover"
                    style={{ backgroundImage: 'var(--atlas-gradient)' }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {t('quickCreate')}
                  </button>
                )}
              {showWorkspaceTools && availableProjects.length > 0 && (
                <DropdownSelect
                  value={selectedProjectId}
                  onChange={chooseProject}
                  ariaLabel={t('selectProject')}
                  leading={<Folder className="h-4 w-4 shrink-0 text-atlas-muted" />}
                  options={[
                    { value: '', label: t('allProjects') },
                    ...availableProjects.map((project) => ({
                      value: project.id,
                      label: project.name,
                    })),
                  ]}
                />
              )}
              <button
                onClick={() => setCommandOpen(true)}
                className="flex h-9 w-full items-center gap-2.5 rounded-lg border border-atlas-border bg-atlas-surface px-3 text-left text-xs text-atlas-muted shadow-sm transition hover:border-atlas-border-strong hover:text-atlas-ink"
              >
                <Search className="h-3.5 w-3.5" />
                <span className="flex-1">{t('search')}</span>
                <span className="flex items-center gap-0.5 rounded border border-atlas-border bg-atlas-subtle px-1.5 py-0.5 font-mono text-[9px]">
                  <Command className="h-2.5 w-2.5" />K
                </span>
              </button>
            </div>

            <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
              {activeArea.groups.map((group, groupIndex) => (
                <div
                  key={group.root?.label || group.label || groupIndex}
                  className={group.nested ? 'border-l border-atlas-border' : ''}
                >
                  {group.label && group.label !== activeArea.label && (
                    <div
                      className={`mb-1.5 truncate px-3 text-[9px] font-medium uppercase tracking-[0.14em] text-atlas-muted/75 ${group.nested ? 'ml-3 flex items-center gap-2' : ''}`}
                    >
                      {group.nested && <Folder className="h-3.5 w-3.5 shrink-0" />}
                      <span className="truncate">{group.label}</span>
                    </div>
                  )}
                  <div className="space-y-1">
                    {group.items.map((item, itemIndex) => (
                      <div key={`${item.to}-${item.label}`}>
                        {item.section && (
                          <div
                            className={`mb-1.5 truncate px-3 text-[9px] font-medium uppercase tracking-[0.14em] text-atlas-muted/75 ${itemIndex > 0 ? 'pt-3' : ''}`}
                          >
                            {item.section}
                          </div>
                        )}
                        <SidebarLink
                          item={item}
                          nested={group.nested}
                          onSelect={() => setMobileSidebarOpen(false)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </nav>

            {isAuthenticated && (
              <div className="mx-3 mb-3 rounded-lg border border-atlas-border bg-atlas-surface p-2.5">
                <div className="text-[9px] font-medium uppercase tracking-[0.12em] text-atlas-muted">
                  {t('organization')}
                </div>
                <div className="mt-1 truncate text-xs font-medium text-atlas-ink">
                  {user?.tenantName || t('atlasWorkspace')}
                </div>
                <div className="mt-0.5 text-[10px] capitalize text-atlas-muted">
                  {roleLabel(user?.role, t)}
                </div>
              </div>
            )}
          </aside>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-screen overflow-hidden bg-atlas-canvas font-sans text-atlas-ink selection:bg-atlas-blue/20">
      <div className="hidden shrink-0 lg:block">{renderSidebar()}</div>
      {mobileSidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            className="absolute inset-0 bg-black/35 backdrop-blur-sm"
            onClick={() => setMobileSidebarOpen(false)}
            aria-label={t('closeNavigation')}
          />
          <div className="atlas-enter relative h-full w-[272px] shadow-atlas-float">
            {renderSidebar(true)}
          </div>
          <button
            onClick={() => setMobileSidebarOpen(false)}
            className="absolute left-[284px] top-3 grid h-9 w-9 place-items-center rounded-lg bg-white text-black shadow-lg"
            aria-label={t('closeNavigation')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="relative z-30 flex h-[42px] shrink-0 items-center justify-between border-b border-atlas-border bg-atlas-surface/95 px-3 backdrop-blur-xl sm:px-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <button
              onClick={() => (isOffCanvasCollapsed ? toggleSidebar() : setMobileSidebarOpen(true))}
              className={`atlas-icon-button ${isOffCanvasCollapsed ? 'lg:inline-grid' : 'lg:hidden'}`}
              aria-label={t('openNavigation')}
            >
              <Menu className="h-4 w-4" />
            </button>
            <div className="hidden items-center gap-1.5 text-xs sm:flex">
              <span className="text-atlas-muted">{appName}</span>
              <ChevronRight className="h-3 w-3 text-atlas-border-strong" />
              <span className="truncate font-medium text-atlas-ink">
                {pageLabel(location.pathname, t)}
              </span>
            </div>
            <div className="truncate text-xs font-medium sm:hidden">
              {pageLabel(location.pathname, t)}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setCommandOpen(true)}
              className="atlas-icon-button sm:hidden"
              aria-label={t('searchAtlas')}
            >
              <Search className="h-4 w-4" />
            </button>
            {isAuthenticated && (
              <div ref={settingsMenuRef} className="relative">
                <button
                  onClick={() => {
                    setSettingsOpen((open) => !open);
                    setProfileOpen(false);
                  }}
                  className="atlas-icon-button"
                  aria-label={t('openSettings')}
                  aria-haspopup="menu"
                  aria-expanded={settingsOpen}
                >
                  <Settings className="h-4 w-4" />
                </button>
                {settingsOpen && (
                  <div
                    role="dialog"
                    aria-label={t('preferences')}
                    className="atlas-enter absolute right-0 top-11 w-[min(300px,calc(100vw-24px))] overflow-visible rounded-lg border border-atlas-border bg-atlas-elevated p-3 shadow-atlas-float"
                  >
                    <div>
                      <div className="text-sm font-medium leading-5 text-atlas-ink">
                        {t('preferences')}
                      </div>
                      <div className="mt-0.5 text-[10px] text-atlas-muted">
                        {t('customizePreferences')}
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="text-[10px] font-medium text-atlas-ink">{t('language')}</div>
                      <DropdownSelect
                        className="mt-1"
                        value={language}
                        onChange={(value) => setLanguage(value as AtlasLanguage)}
                        ariaLabel={t('language')}
                        leading={<Globe2 className="h-3.5 w-3.5 text-atlas-muted" />}
                        options={ATLAS_LANGUAGES.map((option) => ({ ...option }))}
                      />
                    </div>
                    <div className="mt-2.5">
                      <div className="text-[10px] font-medium text-atlas-ink">
                        {t('themePreset')}
                      </div>
                      <DropdownSelect
                        className="mt-1"
                        value={themePreset}
                        onChange={(value) => setThemePreset(value as ThemePreset)}
                        ariaLabel={t('themePreset')}
                        options={THEME_PRESETS.map((preset) => ({
                          value: preset.id,
                          label: preset.name,
                          color: preset.colors[0],
                        }))}
                      />
                    </div>
                    <div className="mt-2.5">
                      <div className="text-[10px] font-medium text-atlas-ink">{t('font')}</div>
                      <DropdownSelect
                        className="mt-1"
                        value={atlasFont}
                        onChange={(value) => setAtlasFont(value as AtlasFont)}
                        ariaLabel={t('font')}
                        options={FONT_OPTIONS.map((font) => ({ value: font.id, label: font.name }))}
                      />
                    </div>
                    <div className="mt-2.5">
                      <div className="text-[10px] font-medium text-atlas-ink">{t('themeMode')}</div>
                      <div className="mt-1 grid grid-cols-3 overflow-hidden rounded-full border border-atlas-border bg-atlas-surface p-0.5">
                        {(['light', 'dark', 'system'] as const).map((mode) => (
                          <button
                            key={mode}
                            onClick={() => setTheme(mode)}
                            className={`h-7 rounded-full text-[10px] font-medium transition ${theme === mode ? 'bg-atlas-subtle text-atlas-ink shadow-sm' : 'text-atlas-muted hover:text-atlas-ink'}`}
                          >
                            {t(mode)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="mt-2.5">
                      <div className="text-[10px] font-medium text-atlas-ink">
                        {t('sidebarCollapseMode')}
                      </div>
                      <div className="mt-1 grid grid-cols-2 overflow-hidden rounded-full border border-atlas-border bg-atlas-surface p-0.5">
                        <button
                          onClick={() => setSidebarCollapseMode('icons')}
                          className={`h-7 rounded-full text-[10px] font-medium transition ${sidebarCollapseMode === 'icons' ? 'bg-atlas-subtle text-atlas-ink shadow-sm' : 'text-atlas-muted hover:text-atlas-ink'}`}
                        >
                          {t('icon')}
                        </button>
                        <button
                          onClick={() => setSidebarCollapseMode('off-canvas')}
                          className={`h-7 rounded-full text-[10px] font-medium transition ${sidebarCollapseMode === 'off-canvas' ? 'bg-atlas-subtle text-atlas-ink shadow-sm' : 'text-atlas-muted hover:text-atlas-ink'}`}
                        >
                          {t('offCanvas')}
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={restoreAppearanceDefaults}
                      className="mt-3 h-8 w-full rounded-full border border-atlas-border bg-atlas-surface text-[10px] font-medium text-atlas-ink transition hover:bg-atlas-subtle"
                    >
                      {t('restoreDefaults')}
                    </button>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={toggleTheme}
              className="atlas-icon-button"
              aria-label={`Use ${resolvedTheme === 'dark' ? 'light' : 'dark'} theme`}
            >
              {resolvedTheme === 'dark' ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </button>
            {isAuthenticated ? (
              <div ref={profileMenuRef} className="relative ml-1">
                <button
                  onClick={() => {
                    setProfileOpen((open) => !open);
                    setSettingsOpen(false);
                  }}
                  className="flex h-9 items-center gap-1 rounded-full border border-transparent px-1 transition hover:border-atlas-border hover:bg-atlas-subtle"
                  aria-label={t('openAccount')}
                  aria-haspopup="menu"
                  aria-expanded={profileOpen}
                >
                  <UserAnimalAvatar
                    seed={user?.id || user?.email || 'atlas-user'}
                    className="h-7 w-7 rounded-full border border-atlas-border"
                    iconClassName="h-4 w-4"
                  />
                  <ChevronDown
                    className={`h-3 w-3 text-atlas-muted transition-transform ${profileOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {profileOpen && (
                  <div
                    role="menu"
                    className="atlas-enter absolute right-0 top-11 w-56 overflow-hidden rounded-xl border border-atlas-border bg-atlas-elevated p-1.5 shadow-atlas-float"
                  >
                    <div className="flex items-center gap-2.5 rounded-lg bg-atlas-subtle p-2">
                      <UserAnimalAvatar
                        seed={user?.id || user?.email || 'atlas-user'}
                        className="h-10 w-10 rounded-full border border-atlas-border bg-atlas-surface"
                        iconClassName="h-6 w-6"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-atlas-ink">
                          {[user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
                            "Dexter user"}
                        </div>
                        <div className="truncate text-xs text-atlas-muted">
                          {roleLabel(user?.role, t)}
                        </div>
                      </div>
                      <Check className="h-4 w-4 shrink-0 text-atlas-ink" />
                    </div>
                    <div className="mt-1 border-t border-atlas-border pt-1">
                      <button
                        onClick={() => {
                          setProfileOpen(false);
                          navigate('/profile');
                        }}
                        className="flex h-9 w-full items-center gap-2.5 rounded-lg px-2.5 text-xs text-atlas-ink hover:bg-atlas-subtle"
                        role="menuitem"
                      >
                        <ShieldCheck className="h-4 w-4" />
                        {t('account')}
                      </button>
                      {!isSchedulingStaff && (
                      <>
                      <button
                        onClick={() => {
                          setProfileOpen(false);
                          navigate('/billing');
                        }}
                        className="flex h-9 w-full items-center gap-2.5 rounded-lg px-2.5 text-xs text-atlas-ink hover:bg-atlas-subtle"
                        role="menuitem"
                      >
                        <CreditCard className="h-4 w-4" />
                        {t('billing')}
                      </button>
                      <button
                        disabled
                        className="flex h-9 w-full items-center gap-2.5 rounded-lg px-2.5 text-xs text-atlas-muted"
                        role="menuitem"
                      >
                        <Bell className="h-4 w-4" />
                        {t('notifications')}
                      </button>
                      </>
                      )}
                    </div>
                    <div className="mt-1 border-t border-atlas-border pt-1">
                      <button
                        onClick={handleLogout}
                        className="flex h-9 w-full items-center gap-2.5 rounded-lg px-2.5 text-xs font-medium text-atlas-ink hover:bg-atlas-subtle"
                        role="menuitem"
                      >
                        <LogOut className="h-4 w-4" />
                        {t('logOut')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="atlas-button-primary">
                {t('signIn')}
              </Link>
            )}
          </div>
        </header>
        <TenantExpirationBanner />
        <TenantQuotaBanner />
        <main className="min-h-0 flex-1 overflow-auto bg-atlas-canvas">
          <div key={location.pathname} className="min-h-full atlas-enter">
            <Outlet />
          </div>
        </main>
      </div>
      <CommandPalette
        open={commandOpen}
        onClose={() => setCommandOpen(false)}
        items={commandItems}
      />
    </div>
  );
}
